import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useDebounce } from 'react-use'
import { PatientMergePage } from './index'

import http from '../../../lib/http'
import FriendlyDate from '../../shared/Date/FriendlyDate'
import { Button, Modal } from '../../shared/NarusMaterial'
import * as Table from '../../claim-history/simple-table'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import { useAppSelector } from '../../../store'
import * as constants from './constants'

type PatientMergeJob = Table.Item & {
    id: string
    main_patient_id: string
    merge_patient_id: string
    status: string
    workflow_id?: string | null
    requested_by?: string | null
    requested_by_email?: string | null
    requested_by_name?: string | null
    main_patient_first_name?: string | null
    main_patient_last_name?: string | null
    merge_patient_first_name?: string | null
    merge_patient_last_name?: string | null
    merge_patient_exists?: boolean | null
    reason?: string | null
    total_resources?: number | null
    processed_resources?: number | null
    failed_resources?: number | null
    created_at?: string | null
    started_at?: string | null
    finished_at?: string | null
    error?: string | null
}

type PatientMergeHistoryResponse = {
    data: PatientMergeJob[]
    meta: {
        total: number
        page: number
        count: number
    }
}

type PatientDeletePreviewResponse = {
    patientId: string
    totals: {
        patient: number
        episodeOfCare: number
        list: number
        patientIdTables: number
        resourceTables: number
        direct: number
        overall: number
        extra: number
    }
}

const mergeStatusOptions = [
    { value: 'queued', label: 'Queued' },
    { value: 'running', label: 'Running' },
    { value: 'completed', label: 'Completed' },
    { value: 'completed_with_errors', label: 'Completed with Errors' },
    { value: 'failed', label: 'Failed' }
]

function formatDate(value?: string | null): JSX.Element | string {
    if (!value) {
        return '—'
    }
    return <FriendlyDate date={value} short />
}

function renderStatus(value: string): JSX.Element {
    const styles: Record<string, string> = {
        queued: 'bg-amber-100 text-amber-800',
        running: 'bg-blue-100 text-blue-800',
        completed: 'bg-emerald-100 text-emerald-800',
        completed_with_errors: 'bg-orange-100 text-orange-800',
        failed: 'bg-red-100 text-red-800'
    }

    const className = styles[value] ?? 'bg-gray-100 text-gray-700'

    return (
        <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${className}`}
        >
            {value.replace(/_/g, ' ')}
        </span>
    )
}

export function PatientMergeHistoryPage(): JSX.Element {
    const [showMerge, setShowMerge] = useState(false)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(20)
    const [statusFilter, setStatusFilter] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [deletingPatientId, setDeletingPatientId] = useState<string | null>(
        null
    )
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [deletePreview, setDeletePreview] =
        useState<PatientDeletePreviewResponse | null>(null)
    const [deleteRow, setDeleteRow] = useState<PatientMergeJob | null>(null)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    let patientMergePermission = useAppSelector(
        (state) => state?.globalVars?.permissions?.patient_merge ?? 'none'
    )

    let user = useAppSelector((state) => state?.globalVars?.user)

    let isAdmin =
        // @ts-ignore // it's okey
        constants.ADMIN_ROLES.includes(user?.app_metadata?.roles?.meta?.role) ?? false

    let canDelete = isAdmin || patientMergePermission === 'write'

    useDebounce(
        () => {
            setDebouncedSearch(searchQuery)
            setPage(0)
        },
        400,
        [searchQuery]
    )

    const query = useQuery({
        queryKey: [
            'patient-merge-history',
            page,
            pageSize,
            statusFilter,
            debouncedSearch
        ],
        queryFn: async () => {
            return await http.getRequest<PatientMergeHistoryResponse>(
                '/admin/patient-merge/history',
                {
                    page,
                    count: pageSize,
                    status: statusFilter ? [statusFilter] : undefined,
                    search: debouncedSearch || undefined
                },
                { clientName: false }
            )
        },
        placeholderData: keepPreviousData,
        retry: false
    })

    const refetchHistory = query.refetch

    const handleDeleteMergePatient = useCallback(
        async (row: PatientMergeJob): Promise<void> => {
            const mergePatientId = row.merge_patient_id
            if (!mergePatientId || !row.merge_patient_exists) {
                return
            }

            setDeletingPatientId(mergePatientId)
            setDeleteError(null)
            setDeleteRow(row)

            try {
                const preview =
                    await http.getRequest<PatientDeletePreviewResponse>(
                        '/admin/patient-delete/preview',
                        { patientId: mergePatientId },
                        { clientName: false }
                    )
                setDeletePreview(preview)
                setDeleteModalOpen(true)
            } catch (err: any) {
                const message =
                    err?.response?.data?.error ??
                    'Failed to start delete workflow.'
                setDeleteError(message)
                setDeleteModalOpen(true)
            } finally {
                setDeletingPatientId(null)
            }
        },
        [refetchHistory]
    )

    const handleConfirmDelete = useCallback(async (): Promise<void> => {
        if (!deleteRow || !deletePreview) {
            return
        }

        const mergePatientId = deleteRow.merge_patient_id
        if (!mergePatientId) {
            return
        }

        setDeletingPatientId(mergePatientId)
        setDeleteError(null)

        try {
            await http.postRequest(
                '/admin/patient-delete',
                {
                    patientId: mergePatientId,
                    mergeJobId: deleteRow.id,
                    confirm: deletePreview.totals.extra > 0
                },
                { clientName: false }
            )

            setDeleteModalOpen(false)
            setDeletePreview(null)
            setDeleteRow(null)
            await refetchHistory()
        } catch (err: any) {
            const message =
                err?.response?.data?.error ?? 'Failed to start delete workflow.'
            setDeleteError(message)
        } finally {
            setDeletingPatientId(null)
        }
    }, [deletePreview, deleteRow, refetchHistory])

    const columns = useMemo<Array<Column<PatientMergeJob>>>(
        () => [
            {
                name: 'id',
                header: 'Job',
                gridArea: '220px',
                getValue: (row) => row.id
            },
            {
                name: 'requested_by',
                header: 'Author ID',
                gridArea: '160px',
                getValue: (row) => row.requested_by ?? '—'
            },
            {
                name: 'requested_by_name',
                header: 'Author Name',
                gridArea: '200px',
                getValue: (row) =>
                    row.requested_by_name || row.requested_by_email || '—'
            },
            {
                name: 'main_patient_id',
                header: 'Primary Patient ID',
                gridArea: '160px',
                getValue: (row) => row.main_patient_id
            },
            {
                name: 'main_patient_first_name',
                header: 'Primary Patient Name',
                gridArea: '200px',
                getValue: (row) => {
                    const name = `${row.main_patient_first_name ?? ''} ${
                        row.main_patient_last_name ?? ''
                    }`.trim()
                    return name || '—'
                }
            },
            {
                name: 'merge_patient_id',
                header: 'Secondary Patient ID',
                gridArea: '160px',
                getValue: (row) => row.merge_patient_id
            },
            {
                name: 'merge_patient_first_name',
                header: 'Secondary Patient Name',
                gridArea: '200px',
                getValue: (row) => {
                    const name = `${row.merge_patient_first_name ?? ''} ${
                        row.merge_patient_last_name ?? ''
                    }`.trim()
                    return name || '—'
                }
            },
            {
                name: 'status',
                header: 'Status',
                gridArea: '140px',
                getValue: (row) => renderStatus(row.status)
            },
            {
                name: 'created_at',
                header: 'Created',
                gridArea: '140px',
                getValue: (row) => formatDate(row.created_at)
            },
            {
                name: 'started_at',
                header: 'Started',
                gridArea: '140px',
                getValue: (row) => formatDate(row.started_at)
            },
            {
                name: 'finished_at',
                header: 'Finished',
                gridArea: '140px',
                getValue: (row) => formatDate(row.finished_at)
            },
            {
                name: 'total_resources',
                header: 'Resources',
                gridArea: '160px',
                getValue: (row) =>
                    `${row.processed_resources ?? 0} / ${
                        row.total_resources ?? 0
                    }`
            },
            {
                name: 'delete_merge_patient',
                header: 'Delete Secondary Patient',
                gridArea: '180px',
                isAction: true,
                getValue: (row) => {
                    if (!row.merge_patient_exists) {
                        return '—'
                    }
                    const isDeleting =
                        deletingPatientId === row.merge_patient_id
                    return (
                        <button
                            className="inline-flex items-center rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                            type="button"
                            disabled={isDeleting || !canDelete}
                            onClick={() => handleDeleteMergePatient(row)}
                        >
                            {isDeleting ? 'Starting...' : 'Delete'}
                        </button>
                    )
                }
            },
            {
                name: 'error',
                header: 'Error',
                gridArea: '2fr',
                getValue: (row) => row.error ?? '—'
            }
        ],
        [deletingPatientId, handleDeleteMergePatient]
    )

    const pagination = useMemo(
        () => ({
            page,
            pageSize,
            totalFiltered: query.data?.meta?.total ?? 0,
            totalUnfiltered: query.data?.meta?.total ?? 0
        }),
        [page, pageSize, query.data?.meta?.total]
    )

    if (showMerge) {
        return <PatientMergePage onBack={() => setShowMerge(false)} />
    }

    return (
        <div className="p-4">
            <PageHeader className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">
                        Patient Merge History
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Track recent merge jobs and their status.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <SearchInput
                        value={searchQuery}
                        placeholder="Search by patient ID, job ID, or requester"
                        onChange={(event) => {
                            setSearchQuery(event.target.value)
                        }}
                    />
                    <button
                        className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-opacity-90"
                        type="button"
                        onClick={() => setShowMerge(true)}
                    >
                        Run New Merge
                    </button>
                </div>
            </PageHeader>

            {deleteModalOpen && (
                <Modal
                    title="Delete patient"
                    actions={[
                        <Button
                            key="cancel"
                            grey
                            label={deleteError ? 'Close' : 'Cancel'}
                            onClick={() => {
                                setDeleteModalOpen(false)
                                setDeletePreview(null)
                                setDeleteRow(null)
                                setDeleteError(null)
                            }}
                        />,
                        deletePreview && !deleteError ? (
                            <Button
                                key="delete"
                                label={
                                    deletingPatientId &&
                                    deletingPatientId ===
                                        deleteRow?.merge_patient_id
                                        ? 'Deleting...'
                                        : 'Delete'
                                }
                                onClick={handleConfirmDelete}
                            />
                        ) : null
                    ].filter(Boolean)}
                    onClose={() => {
                        setDeleteModalOpen(false)
                        setDeletePreview(null)
                        setDeleteRow(null)
                        setDeleteError(null)
                    }}
                >
                    <div className="space-y-3 text-sm text-gray-700">
                        {deleteError ? (
                            <p className="text-red-600">{deleteError}</p>
                        ) : (
                            <>
                                <p>
                                    This action will permanently delete patient{' '}
                                    <span className="font-semibold">
                                        {deleteRow?.merge_patient_id}
                                    </span>{' '}
                                    and related resources.
                                </p>
                                <p>
                                    Patient:{' '}
                                    {deletePreview?.totals.patient ?? 0},
                                    EpisodeOfCare:{' '}
                                    {deletePreview?.totals.episodeOfCare ?? 0},
                                    List: {deletePreview?.totals.list ?? 0}
                                </p>
                                {(deletePreview?.totals.extra ?? 0) > 0 && (
                                    <p className="text-red-600">
                                        Other resources to delete:{' '}
                                        {deletePreview?.totals.extra ?? 0} (
                                        total{' '}
                                        {deletePreview?.totals.overall ?? 0})
                                    </p>
                                )}
                                <p className="text-xs text-gray-500">
                                    This action is unsafe and cannot be undone
                                    without manual rollback.
                                </p>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            <Table.Table
                data={query.data?.data ?? []}
                queryStatus={query.status}
                fetchStatus={query.fetchStatus}
                error={query.error as Error | undefined}
                noDataPlaceholder="No merge jobs found"
                columns={columns}
                pagination={pagination}
                onPageChange={(value: number) => setPage(value - 1)}
                onPageSizeChange={(value: number) => {
                    setPageSize(value)
                    setPage(0)
                }}
                filters={[
                    {
                        name: 'status',
                        value: statusFilter ? [statusFilter] : [],
                        radio: true,
                        hideSearch: true,
                        options: mergeStatusOptions
                    }
                ]}
                onFilterChange={(_field, values) => {
                    setStatusFilter(values[0] ? String(values[0]) : null)
                    setPage(0)
                }}
                onFiltersClear={() => {
                    setStatusFilter(null)
                    setPage(0)
                }}
            />
        </div>
    )
}
